import { z } from "zod";

import type { components } from "@/lib/generated/openapi";

type Schemas = components["schemas"];

const utcTime = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/);
const dateTime = z.iso.datetime({ offset: true });
const nonBlank = z.string().trim().min(1);
const identifier = z.string().min(1).max(120);
const weekday = z.enum(["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"]);
const frequency = z.enum(["daily", "weekly", "monthly"]);
const misfirePolicy = z.enum(["latest_only", "catch_up", "skip"]);
const invalidDayPolicy = z.enum(["last_day", "skip"]);
const userRole = z.enum([
  "CEO",
  "Production Manager",
  "Center Manager",
  "Veterinarian",
  "Quality Manager",
  "Operations",
  "Finance",
]);
const recipientUserId = z.string().trim().min(1).max(120);

export const dailyPodcastRepublishResponseSchema = z
  .object({
    occurrenceId: nonBlank,
    episodeId: nonBlank,
    publicationId: nonBlank,
    skippedWhatsapp: z.boolean(),
    ragUploadAttempted: z.boolean(),
    ragUploadSkippedReason: z.string().nullable().optional(),
    updatedAt: dateTime,
    actorId: z.string().nullable().optional(),
    notes: z.array(z.string()).default([]),
  })
  .loose();

function uniqueArray<Item extends z.ZodType>(item: Item, maximum: number, message: string) {
  return z
    .array(item)
    .max(maximum)
    .superRefine((values, context) => {
      if (new Set(values).size !== values.length) {
        context.addIssue({ code: "custom", message });
      }
    });
}

const recipientRoles = uniqueArray(userRole, 20, "Recipient roles must be unique.");
const recipientUserIds = uniqueArray(recipientUserId, 500, "Recipient user IDs must be unique.");

function validateReportDeliveryAudience(
  value: { includedUserIds?: string[]; excludedUserIds?: string[] },
  context: z.RefinementCtx,
) {
  const excluded = new Set(value.excludedUserIds ?? []);
  if ((value.includedUserIds ?? []).some((userId) => excluded.has(userId))) {
    context.addIssue({
      code: "custom",
      path: ["includedUserIds"],
      message: "Included and excluded users cannot overlap.",
    });
  }
}

const weeklySchedule = z
  .object({ weekdays: z.array(weekday).min(1).max(7) })
  .strict()
  .superRefine((value, context) => {
    if (new Set(value.weekdays).size !== value.weekdays.length) {
      context.addIssue({ code: "custom", path: ["weekdays"], message: "Weekdays must be unique." });
    }
  });

const monthlySchedule = z
  .object({ day: z.number().int().min(1).max(31), invalidDayPolicy: invalidDayPolicy.optional() })
  .strict();

const keyMetricSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    meaning: z.string().trim().min(1).max(2_000),
  })
  .strict();

const interpretationRuleSchema = z
  .object({
    condition: z.string().trim().min(1).max(2_000),
    interpretation: z.string().trim().min(1).max(2_000),
  })
  .strict();

export const reportContextCreateRequestSchema = z
  .object({
    purpose: z.string().trim().min(1).max(2_000),
    siteIds: z.array(identifier).max(20).optional(),
    audience: z.string().trim().min(1).max(200).nullable().optional(),
    readingOrder: z.string().trim().min(1).max(2_000).nullable().optional(),
    keyMetrics: z.array(keyMetricSchema).max(20).optional(),
    interpretationRules: z.array(interpretationRuleSchema).max(20).optional(),
    expertProfile: z.string().trim().min(1).max(2_000).nullable().optional(),
    expectedRecommendations: z.string().trim().min(1).max(2_000).nullable().optional(),
    communicationStyle: z.string().trim().min(1).max(2_000).nullable().optional(),
    additionalGuidance: z.string().trim().min(1).max(4_000).nullable().optional(),
  })
  .strict();

export const reportContextWriteRequestSchema = reportContextCreateRequestSchema
  .extend({ expectedRevision: z.number().int().min(0) })
  .strict();

const scheduleRequiredFields = {
  reportType: nonBlank,
  reportId: nonBlank,
  reportName: nonBlank,
  pageName: nonBlank,
  timeOfDay: utcTime,
  workspaceId: identifier,
} as const;

const scheduleOptionalFields = {
  frequency,
  pageDisplayName: z.string().nullable(),
  weekly: weeklySchedule.nullable(),
  monthly: monthlySchedule.nullable(),
  locale: z.string().nullable(),
  active: z.boolean(),
  misfirePolicy,
  catchUpLimit: z.number().int().min(1).max(100),
  recipientRoles,
  includedUserIds: recipientUserIds,
  excludedUserIds: recipientUserIds,
} as const;

function validateCadence(
  value: { frequency?: z.infer<typeof frequency>; weekly?: unknown; monthly?: unknown },
  context: z.RefinementCtx,
) {
  const selectedFrequency = value.frequency ?? "daily";
  const weekly = value.weekly ?? null;
  const monthly = value.monthly ?? null;
  if (selectedFrequency === "daily" && (weekly !== null || monthly !== null)) {
    context.addIssue({
      code: "custom",
      path: ["frequency"],
      message: "Daily schedules cannot define weekly or monthly cadence.",
    });
  }
  if (selectedFrequency === "weekly" && (weekly === null || monthly !== null)) {
    context.addIssue({ code: "custom", path: ["weekly"], message: "Weekly schedules require weekly cadence only." });
  }
  if (selectedFrequency === "monthly" && (monthly === null || weekly !== null)) {
    context.addIssue({ code: "custom", path: ["monthly"], message: "Monthly schedules require monthly cadence only." });
  }
}

export const powerBIScheduleUpdateRequestSchema = z
  .object({
    ...scheduleRequiredFields,
    ...scheduleOptionalFields,
    recipientRoles: scheduleOptionalFields.recipientRoles.optional(),
    includedUserIds: scheduleOptionalFields.includedUserIds.optional(),
    excludedUserIds: scheduleOptionalFields.excludedUserIds.optional(),
  })
  .strict()
  .superRefine(validateCadence)
  .superRefine(validateReportDeliveryAudience);

export const powerBIScheduleCreateRequestSchema = z
  .object({
    ...scheduleRequiredFields,
    frequency: frequency.optional(),
    pageDisplayName: scheduleOptionalFields.pageDisplayName.optional(),
    weekly: scheduleOptionalFields.weekly.optional(),
    monthly: scheduleOptionalFields.monthly.optional(),
    locale: scheduleOptionalFields.locale.optional(),
    active: scheduleOptionalFields.active.optional(),
    misfirePolicy: scheduleOptionalFields.misfirePolicy.optional(),
    catchUpLimit: scheduleOptionalFields.catchUpLimit.optional(),
    recipientRoles: scheduleOptionalFields.recipientRoles.optional(),
    includedUserIds: scheduleOptionalFields.includedUserIds.optional(),
    excludedUserIds: scheduleOptionalFields.excludedUserIds.optional(),
    context: reportContextCreateRequestSchema,
  })
  .strict()
  .superRefine(validateCadence)
  .superRefine(validateReportDeliveryAudience);

export const reportDeliveryAudienceUpdateRequestSchema = z
  .object({
    recipientRoles,
    includedUserIds: recipientUserIds,
    excludedUserIds: recipientUserIds,
  })
  .strict()
  .superRefine(validateReportDeliveryAudience);

export const dailyPodcastPolicyUpdateRequestSchema = z
  .object({
    generateDailyAt: utcTime.nullable().optional(),
    completenessDeadlineOffsetMinutes: z.number().int().min(1).max(10_080).nullable().optional(),
    onIncompleteDeadline: z.enum(["publish_partial", "fail", "wait_for_operator"]).nullable().optional(),
    allowEmpty: z.boolean().nullable().optional(),
    lateArrivalPolicy: z.enum(["next_daily", "regenerate", "manual_review"]).nullable().optional(),
    recipientUserIds: recipientUserIds.optional(),
  })
  .strict();

const reportContextResponseSchema = z
  .object({
    scheduleId: identifier,
    revision: z.number().int().min(1),
    purpose: z.string().trim().min(1).max(2_000),
    siteIds: z.array(identifier).max(20),
    audience: z.string().trim().min(1).max(200).nullable(),
    readingOrder: z.string().trim().min(1).max(2_000).nullable(),
    keyMetrics: z.array(keyMetricSchema).max(20),
    interpretationRules: z.array(interpretationRuleSchema).max(20),
    expertProfile: z.string().trim().min(1).max(2_000).nullable(),
    expectedRecommendations: z.string().trim().min(1).max(2_000).nullable(),
    communicationStyle: z.string().trim().min(1).max(2_000).nullable(),
    additionalGuidance: z.string().trim().min(1).max(4_000).nullable(),
    createdAt: dateTime,
    updatedAt: dateTime,
  })
  .strict();

const powerBIScheduleSchema = z
  .object({
    id: z.string().min(1),
    reportType: z.string(),
    frequency,
    reportId: z.string(),
    reportName: z.string(),
    pageName: z.string(),
    pageDisplayName: z.string().nullable(),
    timeOfDay: utcTime.nullable(),
    weekly: weeklySchedule.nullable(),
    monthly: monthlySchedule.extend({ invalidDayPolicy }).nullable(),
    workspaceId: identifier,
    locale: z.string().nullable(),
    active: z.boolean(),
    revision: z.number().int().min(1),
    misfirePolicy,
    catchUpLimit: z.number().int().min(1).max(100),
    recipientRoles,
    includedUserIds: recipientUserIds,
    excludedUserIds: recipientUserIds,
    nextMaterializationAt: dateTime.nullable(),
    cadenceEffectiveAt: dateTime.nullable(),
    createdAt: dateTime,
    updatedAt: dateTime,
  })
  .loose();

export const powerBIScheduleCreateResponseSchema = z
  .object({ schedule: powerBIScheduleSchema, context: reportContextResponseSchema })
  .loose();
export const powerBIScheduleResponseSchema = z.object({ schedule: powerBIScheduleSchema }).loose();
export const powerBIScheduleListResponseSchema = z.object({ schedules: z.array(powerBIScheduleSchema) }).loose();
export { powerBIScheduleSchema, reportContextResponseSchema };

const dailyPolicySchema = z
  .object({
    id: z.literal("daily-podcast-policy"),
    generateDailyAt: utcTime.nullable(),
    revision: z.number().int().min(0),
    completenessDeadlineOffsetMinutes: z.number().int().min(1).max(10_080),
    onIncompleteDeadline: z.enum(["publish_partial", "fail", "wait_for_operator"]),
    allowEmpty: z.boolean(),
    lateArrivalPolicy: z.enum(["next_daily", "regenerate", "manual_review"]),
    recipientUserIds,
    createdAt: dateTime,
    updatedAt: dateTime,
  })
  .loose();
export const dailyPodcastPolicyResponseSchema = z.object({ policy: dailyPolicySchema }).loose();

const whatsAppUserMappingSchema = z
  .object({
    id: identifier,
    phoneNumber: nonBlank,
    userId: identifier,
    center: z.string().nullable().optional(),
    role: userRole.nullable().optional(),
    active: z.boolean(),
    activatedAt: dateTime.nullable().optional(),
    reportPodcastEnabled: z.boolean(),
    reportPodcastEnabledAt: dateTime.nullable(),
    dailyPodcastEnabled: z.boolean(),
    dailyPodcastEnabledAt: dateTime.nullable(),
  })
  .loose();

export const whatsAppUserMappingCreateRequestSchema = z
  .object({
    phoneNumber: nonBlank,
    userId: identifier,
    center: z.string().trim().min(1).nullable().optional(),
    role: userRole.nullable().optional(),
    active: z.boolean().optional(),
    reportPodcastEnabled: z.boolean().optional(),
    dailyPodcastEnabled: z.boolean().optional(),
  })
  .strict();

export const whatsAppUserMappingUpdateRequestSchema = z
  .object({
    phoneNumber: nonBlank,
    userId: identifier,
    center: z.string().trim().min(1).nullable().optional(),
    role: userRole.nullable().optional(),
    active: z.boolean(),
  })
  .strict();

export const whatsAppDeliverySettingsUpdateRequestSchema = z
  .object({
    active: z.boolean().optional(),
    reportPodcastEnabled: z.boolean().optional(),
    dailyPodcastEnabled: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, { message: "At least one delivery setting is required." });

export const whatsAppUserMappingResponseSchema = z.object({ mapping: whatsAppUserMappingSchema }).loose();
export const whatsAppUserMappingListResponseSchema = z.object({ mappings: z.array(whatsAppUserMappingSchema) }).loose();

const frozenReferenceSchema = z
  .object({
    kind: z.enum(["organization", "site", "report"]),
    logicalId: identifier,
    revision: z.number().int().min(1),
  })
  .strict();

export const contextPreviewResponseSchema = z
  .object({
    componentRevisions: z.array(frozenReferenceSchema),
    modelInputs: z.array(
      z
        .object({
          modelPass: z.enum(["contextual_analysis", "daily_synthesis", "editorial_script"]),
          systemPromptHash: z.string().regex(/^[a-f0-9]{64}$/),
          data: z.record(z.string(), z.unknown()),
        })
        .strict(),
    ),
  })
  .strict();

const modelPassSchema = z
  .object({
    provider: nonBlank,
    model: nonBlank,
    promptTemplate: z.string().min(1),
    systemPrompt: z.string().min(1),
    temperature: z.number().min(0).max(2),
    thinkingMode: z.enum(["disabled", "provider_default"]),
  })
  .loose();

const speechSchema = z
  .object({
    provider: z.enum(["vibevoice-1.5b", "pocket-tts", "pipeline-tts"]),
    model: z.string().nullable().optional(),
    language: z.string(),
    options: z.record(z.string(), z.unknown()).optional(),
  })
  .loose();

const mediaSchema = z
  .object({
    codec: z.string(),
    preset: z.string(),
    audioBitrate: z.string(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    fps: z.number().int().positive(),
    logoWidth: z.number().int().positive(),
    logoMargin: z.number().int().min(0),
    brandingAssetSha256: z.string(),
  })
  .loose();

const reportPipelineSchema = z
  .object({
    visualDataExtraction: z.object({ requestedRows: z.number().int().positive() }).loose(),
    contextualAnalysis: modelPassSchema,
    editorialScript: modelPassSchema,
    speech: speechSchema,
    media: mediaSchema,
  })
  .loose();

const dailyPipelineSchema = z
  .object({
    dailySynthesis: modelPassSchema,
    editorialScript: modelPassSchema,
    speech: speechSchema,
    media: mediaSchema,
  })
  .loose();

const reportConfigSchema = z
  .object({
    kind: z.literal("report"),
    organizationRevision: frozenReferenceSchema,
    reportContextRevision: frozenReferenceSchema,
    siteRevisions: z.array(frozenReferenceSchema),
    scheduleId: z.string(),
    workspaceId: z.string(),
    powerbiReportId: z.string(),
    pageName: z.string(),
    locale: z.string().nullable(),
    pipeline: reportPipelineSchema,
  })
  .strict();

const dailyConfigSchema = z
  .object({
    kind: z.literal("daily"),
    organizationRevision: frozenReferenceSchema,
    siteRevisions: z.array(frozenReferenceSchema),
    mode: z.enum(["fixed_time", "readiness"]),
    windowStartAt: dateTime,
    windowEndAt: dateTime,
    expectedReportOccurrenceKeys: z.array(z.string()),
    completenessDeadlineAt: dateTime,
    onIncompleteDeadline: z.enum(["publish_partial", "fail", "wait_for_operator"]),
    allowEmpty: z.boolean(),
    lateArrivalPolicy: z.enum(["next_daily", "regenerate", "manual_review"]),
    pipeline: dailyPipelineSchema,
  })
  .loose();

const stageNameSchema = z.enum([
  "powerbi_export_start",
  "powerbi_export_wait",
  "powerbi_export_download",
  "collect_daily_inputs",
  "extract_report_visual_data",
  "assemble_report_context",
  "analyze_report_context",
  "generate_report_script",
  "assemble_daily_context",
  "synthesize_daily_brief",
  "generate_daily_script",
  "synthesize_audio",
  "normalize_audio",
  "render_video",
  "publish",
]);

const occurrenceStatusSchema = z.enum([
  "pending",
  "active",
  "waiting",
  "retry_wait",
  "failed_terminal",
  "cancelled",
  "published",
]);
const dailyCompletenessSchema = z.enum(["complete", "partial", "empty"]);

const workflowCheckpointSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("powerbi_export_started"), exportId: nonBlank }).loose(),
  z.object({ kind: z.literal("powerbi_export_ready"), exportId: nonBlank }).loose(),
  z
    .object({ kind: z.literal("artifact"), role: nonBlank, artifact: z.object({ artifactId: nonBlank }).loose() })
    .loose(),
  z.object({ kind: z.literal("script"), transcript: z.string() }).loose(),
  z.object({ kind: z.literal("daily_inputs"), completeness: dailyCompletenessSchema }).loose(),
  z
    .object({
      kind: z.literal("visual_data_extraction"),
      visualDataExtractionId: nonBlank,
      documentHash: nonBlank,
      rawResponseArtifact: z.object({ artifactId: nonBlank }).loose(),
      visualDatasetArtifact: z.object({ artifactId: nonBlank }).loose(),
    })
    .loose(),
  z.object({ kind: z.literal("generation_context"), contextSnapshotId: nonBlank }).strict(),
  z
    .object({
      kind: z.literal("analysis_brief"),
      analysisBriefId: nonBlank,
      documentHash: nonBlank,
      toolTraceArtifact: z.object({ artifactId: nonBlank }).loose(),
    })
    .loose(),
  z
    .object({
      kind: z.literal("daily_synthesis_brief"),
      dailySynthesisBriefId: nonBlank,
      documentHash: nonBlank,
    })
    .loose(),
  z.object({ kind: z.literal("published"), publicationId: nonBlank }).loose(),
]);

const workflowStageStateSchema = z
  .object({
    stageVersion: nonBlank,
    checkpoint: workflowCheckpointSchema.nullable().optional(),
  })
  .loose();

export const workflowOccurrenceAcceptedSchema = z
  .object({
    id: z.string(),
    occurrenceKey: z.string(),
    kind: z.enum(["report", "daily"]),
    status: occurrenceStatusSchema,
    currentStage: stageNameSchema.nullable(),
    nextActionAt: dateTime.nullable(),
    createdAt: dateTime,
  })
  .loose();

const workflowSourceScheduleSchema = z
  .object({
    id: z.string(),
    label: z.string().nullable().optional(),
    pageName: z.string(),
    pageDisplayName: z.string().nullable().optional(),
  })
  .loose();

const workflowOccurrenceListItemSchema = workflowOccurrenceAcceptedSchema.extend({
  sourceSchedule: workflowSourceScheduleSchema.nullable().optional(),
});

const workflowOccurrenceSchema = z
  .object({
    id: z.string(),
    occurrenceKey: z.string(),
    requestFingerprint: z.string(),
    kind: z.enum(["report", "daily"]),
    status: occurrenceStatusSchema,
    trigger: z.object({ kind: z.enum(["scheduled", "daily_policy", "manual"]) }).loose(),
    configSnapshot: z.discriminatedUnion("kind", [reportConfigSchema, dailyConfigSchema]),
    sourceScheduleLabel: nonBlank.nullable(),
    stages: z.partialRecord(stageNameSchema, workflowStageStateSchema),
    currentStage: stageNameSchema.nullable(),
    nextActionAt: dateTime.nullable(),
    publicationId: z.string().nullable(),
    revision: z.number().int().min(1),
    createdAt: dateTime,
    updatedAt: dateTime,
    publishedAt: dateTime.nullable(),
  })
  .loose();

export const workflowOccurrenceListResponseSchema = z
  .object({ occurrences: z.array(workflowOccurrenceListItemSchema), nextCursor: z.string().nullable() })
  .loose();
export const workflowOccurrenceDetailResponseSchema = z
  .object({
    occurrence: workflowOccurrenceSchema,
    attempts: z.array(z.object({ id: z.string(), stageName: stageNameSchema }).loose()),
    auditEvents: z.array(z.object({ id: z.string(), action: z.string() }).loose()),
    outboxEvents: z.array(z.object({ id: z.string(), publicRecordIds: z.array(z.string()) }).loose()),
  })
  .loose();

const generationArtifactSchema = z
  .object({
    artifactId: identifier,
    storageBackend: z.enum(["mongo", "minio"]),
    objectKey: nonBlank,
    contentType: nonBlank,
    sizeBytes: z.number().int().min(0),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    createdAt: dateTime,
  })
  .loose();

const executionSummarySchema = z
  .object({
    calls: z.array(
      z
        .object({
          requestKind: nonBlank,
          providerRequestId: z.string().nullable().optional(),
          nativeFinishReason: z.string().nullable().optional(),
          termination: z.enum([
            "complete",
            "tool_calls",
            "output_limit",
            "refused",
            "content_filtered",
            "blocked",
            "unexpected",
          ]),
          inputTokens: z.number().int().min(0).nullable().optional(),
          outputTokens: z.number().int().min(0).nullable().optional(),
          totalTokens: z.number().int().min(0).nullable().optional(),
          modelVersion: z.string().nullable().optional(),
        })
        .loose(),
    ),
    cacheHits: z.number().int().min(0),
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    totalTokens: z.number().int().min(0),
  })
  .loose();

const contextSnapshotSchema = z
  .object({
    id: identifier,
    workflowOccurrenceId: identifier,
    stageAttemptId: identifier,
    purpose: z.enum(["report_analysis", "daily_synthesis"]),
    componentRevisions: z.array(frozenReferenceSchema),
    resolvedContext: z
      .object({
        editorialGuidance: z
          .object({
            purpose: z.string().trim().min(1).max(2_000).nullable().optional(),
            audience: z.string().trim().min(1).max(200).nullable().optional(),
            readingOrder: z.string().trim().min(1).max(2_000).nullable().optional(),
            communicationStyle: z.string().trim().min(1).max(2_000).nullable().optional(),
          })
          .strict(),
        components: z.array(
          z
            .object({
              reference: frozenReferenceSchema,
              selectedContent: z.record(z.string(), z.unknown()),
            })
            .strict(),
        ),
      })
      .strict(),
    createdAt: dateTime,
  })
  .strict();

const visualDataExtractionSchema = z
  .object({
    id: identifier,
    workflowOccurrenceId: identifier,
    stageAttemptId: identifier,
    scheduleId: identifier,
    workspaceId: identifier,
    reportId: identifier,
    pageName: nonBlank,
    powerbiDatasetId: nonBlank,
    requestedRows: z.number().int().positive(),
    contentDigest: nonBlank,
    factsDigestSha256: nonBlank,
    reportStateSha256: nonBlank,
    reportStateWarnings: z.array(z.string()),
    rawResponseArtifact: generationArtifactSchema,
    visualDatasetArtifact: generationArtifactSchema,
    coverage: z
      .object({
        totalVisuals: z.number().int().min(0),
        exportedVisuals: z.number().int().min(0),
        usableVisuals: z.number().int().min(0),
        failedVisuals: z.number().int().min(0),
        timedOutVisuals: z.number().int().min(0),
        skippedVisuals: z.number().int().min(0),
        excludedVisuals: z.number().int().min(0),
        rowLimitReachedVisuals: z.number().int().min(0),
        sourceRows: z.number().int().min(0),
        datasetRows: z.number().int().min(0),
        reportStateComplete: z.boolean(),
      })
      .loose(),
    visualMappings: z.array(
      z.object({ datasetVisualId: nonBlank, sourceVisualId: nonBlank, sourceDataSha256: nonBlank }).loose(),
    ),
    documentHash: nonBlank,
    createdAt: dateTime,
  })
  .loose();

const analysisBriefSchema = z
  .object({
    id: identifier,
    workflowOccurrenceId: identifier,
    stageAttemptId: identifier,
    scheduleId: identifier,
    visualDataExtractionId: identifier,
    contextSnapshotId: identifier,
    payload: z
      .object({
        executiveSummary: z.string(),
        findings: z.array(z.object({ findingId: identifier }).loose()),
        overallConfidence: z.number(),
        missingInformation: z.array(z.string()),
      })
      .loose(),
    modelPass: modelPassSchema,
    executionSummary: executionSummarySchema,
    toolTraceArtifact: generationArtifactSchema,
    documentHash: nonBlank,
    createdAt: dateTime,
  })
  .loose();

const dailySynthesisBriefSchema = z
  .object({
    id: identifier,
    workflowOccurrenceId: identifier,
    stageAttemptId: identifier,
    contextSnapshotId: identifier,
    sourceAnalysisBriefIds: z.array(identifier),
    payload: z
      .object({
        executiveSummary: z.string(),
        rankedFindings: z.array(z.object({ findingId: identifier }).loose()),
        crossReportPatterns: z.array(z.string()),
        missingInformation: z.array(z.string()),
      })
      .loose(),
    modelPass: modelPassSchema,
    executionSummary: executionSummarySchema,
    documentHash: nonBlank,
    createdAt: dateTime,
  })
  .loose();

export const generationEvidenceResponseSchema = z
  .object({
    workflowOccurrenceId: identifier,
    modelPasses: z.array(modelPassSchema),
    visualDataExtraction: visualDataExtractionSchema.nullable(),
    contextSnapshots: z.array(contextSnapshotSchema),
    analysisBrief: analysisBriefSchema.nullable(),
    dailySynthesisBrief: dailySynthesisBriefSchema.nullable(),
  })
  .loose();

export type PowerBIScheduleCreateRequest = z.infer<typeof powerBIScheduleCreateRequestSchema>;
export type PowerBIScheduleUpdateRequest = z.infer<typeof powerBIScheduleUpdateRequestSchema>;
export type ReportContextCreateRequest = z.infer<typeof reportContextCreateRequestSchema>;
export type ReportDeliveryAudienceUpdateRequest = z.infer<typeof reportDeliveryAudienceUpdateRequestSchema>;
export type WhatsAppDeliverySettingsUpdateRequest = z.infer<typeof whatsAppDeliverySettingsUpdateRequestSchema>;

export function parseContract(schema: z.ZodType, value: unknown): unknown {
  return schema.parse(value);
}

export type CanonicalSchemas = Pick<
  Schemas,
  | "PowerBISchedule"
  | "PowerBIScheduleCreateResponse"
  | "PowerBIScheduleResponse"
  | "PowerBIScheduleListResponse"
  | "ReportDeliveryAudienceUpdateRequest"
  | "DailyPodcastPolicyResponse"
  | "WhatsAppDeliverySettingsUpdateRequest"
  | "WhatsAppUserMappingResponse"
  | "WhatsAppUserMappingListResponse"
  | "ContextPreviewResponse"
  | "GenerationEvidenceResponse"
  | "WorkflowOccurrenceAcceptedResponse"
  | "WorkflowOccurrenceListResponse"
  | "WorkflowOccurrenceDetailResponse"
>;
